import Head from "next/head";
import Link from "next/link";
import {makeStyles} from "@material-ui/styles";
import React, {useContext, useEffect, useRef, useState} from "react";
import Cookies from 'js-cookie';
import {useTranslation} from "tools/i18n";
import triggerObject from "./chatTriggers.json";
import {useRouter} from "next/router";
import {CircularProgress} from "@material-ui/core";
import {TrackingContext} from "../providers/tracking";
import theme from '../theme';
import useSessionStore from '@services/stores/SessionStore';
import { base64Encode } from '@services/Utils';

const useStyles = makeStyles({
    '@keyframes typingAnimation': {
        '0%': {
            transform: 'translateY(0px)',
            backgroundColor: '#555'
        },
        '28%': {
            transform: 'translateY(-4px)',
            backgroundColor: '#777'
        },
        '44%': {
            transform: 'translateY(0px)',
            backgroundColor: '#999'
        }
    },
    '@keyframes slideIn': {
        '0%': {
            transform: 'translateY(50px)',
            opacity: 0
        },
        '100%': {
            transform: 'translateY(0px)',
            opacity: 1
        }
    },
	chatBubble: {
	    position: 'fixed',
        display: 'none',
        alignItems: 'center',
        opacity: 1,
        bottom: 25,
        right: 25,
        zIndex: 990,
        padding: 0,
        backgroundColor: 'rgb(33, 33, 33)',
        borderRadius: '50%',
        border: 'none',
        fontSize: 16,
        fontWeight: 600,
        letterSpacing: 1,
        color: theme.palette.primary.background,
        cursor: 'pointer',
        '&.active': {
	        display: 'flex'
        }
	},
    typing: {
        position: 'absolute',
        top: 36,
        left: -28,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        height: 27,
        backgroundColor: '#f2f2f2',
        padding: '5px 10px',
        borderRadius: 10,
        borderBottomRightRadius: 2
    },
    dot: {
        height: 5,
        marginRight: 4,
        verticalAlign: 'middle',
        width: 5,
        display: 'inline-block',
        backgroundColor: theme.palette.primary.lowlight,
        borderRadius: '50%',
        animation: '$typingAnimation 1.8s infinite ease-in-out',
        '&:last-child': {
            marginRight: 0
        },
        '&:nth-child(1)': {
            animationDelay: '200ms'
        },
        '&:nth-child(2)': {
            animationDelay: '400ms'
        },
        '&:nth-child(3)': {
            animationDelay: '600ms'
        }
    },
    profiles: {
        position: 'relative',
        height: 60,
        width: 60
    },
    profile: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 60,
        width: 60,
        backgroundColor: theme.palette.primary.background,
        borderRadius: '50%',
        overflow: 'hidden'
    },
    onlineIndicator: {
	    width: 12,
        height: 12,
        position: 'absolute',
        right: 4,
        bottom: 2,
        zIndex: 10,
        borderRadius: 14,
        backgroundColor: '#3BD821',
        display: 'block',
        visibility: 'visible',
        boxShadow: '0px 0px 0px 2px rgba(59,216,33, 0.65)'
    },
    preChatForm: {
        position: 'fixed',
        bottom: 110,
        right: 16,
        zIndex: 990,
        display: 'none',
        flexDirection: 'column',
        padding: '66px 20px 25px',
        width: 342,
        boxShadow: '0px 0px 15px -2px rgb(0,0,0,0.12)',
        borderRadius: '0.571429rem',
        backgroundColor: theme.palette.primary.background,
        overflow: 'hidden',
        animation: '$slideIn 0.15s 1 ease-in-out',
        animationFillMode: 'forward',
        '&.open': {
            display: 'flex'
        }
    },
    title: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        padding: '0.571429rem 20px',
        color: theme.palette.primary.color,
        fontWeight: 700,
        textAlign: 'center',
        letterSpacing: '0.0214286rem',
        lineHeight: 2.15,
        fontSize: 15,
        margin: '0 0 2px 0'
    },
    titleIndicator: {
        width: 10,
        height: 10,
        marginTop: 2,
        marginRight: 10,
        zIndex: 10,
        borderRadius: 14,
        backgroundColor: '#3BD821',
        display: 'block',
        visibility: 'visible',
        boxShadow: '0px 0px 0px 2px rgba(59,216,33, 0.65)',
        '&.offline': {
            backgroundColor: '#fb4b4b',
            boxShadow: '0px 0px 0px 2px rgba(251,75,75, 0.45)',
        }
    },
    titleMinimize: {
        position: 'absolute',
        top: 20,
        right: 20,
        display: 'flex',
        padding: 0,
        backgroundColor: 'transparent',
        color: theme.palette.primary.background,
        border: 'none',
        cursor: 'pointer',
        lineHeight: 'inherit'
    },
    fields: {
        marginBottom: 16
    },
    label: {
        display: 'flex',
        flexDirection: 'column',
        color: 'rgb(47, 57, 65)',
        fontSize: 14,
        fontWeight: 600
    },
    input: {
        border: '1px solid rgb(216, 220, 222)',
        borderRadius: 4,
        width: '100%',
        padding: '10px 12px',
        marginTop: 8,
        minHeight: 40,
        lineHeight: 1.28571,
        fontSize: 16,
        backgroundColor: theme.palette.primary.background,
        color: 'rgb(47, 57, 65)',
        cursor: 'auto'
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexDirection: 'column'
    },
    startChat: {
        width: '100%',
        margin: '0em 0.25em 0em 0em',
        padding: '0.78571429em 1.5em 0.78571429em',
        lineHeight: '24px',
        fontWeight: 400,
        fontSize: 15,
        fontFamily: '"Open Sans", sans-serif',
        backgroundColor: '#1b1c1d',
        color: '#ffffff',
        display: 'inline-block',
        minHeight: '1em',
        border: 'none',
        verticalAlign: 'baseline',
        textTransform: 'none',
        fontStyle: 'normal',
        textAlign: 'center',
        textDecoration: 'none',
        borderRadius: '0.28571429rem',
        cursor: 'pointer'
    },
    triggerContent: {
        display: 'flex',
        alignItems: 'flex-end'
    },
    triggerText: {
        alignSelf: 'flex-start',
        padding: '8px 16px',
        marginLeft: 6,
        borderRadius: 16,
        fontSize: 14,
        backgroundColor: '#e9ebed'
    },
    chatDisclaimer: {
        marginBottom: 0,
        fontStyle: 'italic',
        fontSize: 12,
        textAlign: 'center'
    },
    triggerActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 40
    },
    triggerClose: {
        padding: '2px 0',
        fontSize: 13,
        fontWeight: 400,
        marginRight: 20,
        alignSelf: 'center',
        cursor: 'pointer',
        background: '0 0',
        border: 0,
        appearance: 'none',
        lineHeight: 1,
        color: '#000',
        textShadow: '0 1px 0 #fff',
        opacity: .5
    }
}, {name: "ZendeskChat"});

export default function ZendeskChat() {
    const classes = useStyles();
    const router = useRouter();
    const {t} = useTranslation(['popup', 'chat-triggers']);
    const [chatLoaded, setChatLoaded] = useState(false);
    const [scriptLoading, setScriptLoading] = useState(false);
    const [status, setStatus] = useState('offline');
    const [preChatForm, setPreChatForm] = useState({open: false, email: '', profile: 0});
    const [trigger, setTrigger] = useState({text: false, tag: false, priority: 0});
    const [offlinePreChat, setOfflinePreChat] = useState(false)
    const {actions: trackingActions} = useContext(TrackingContext);
    const [triggerOpen, setTriggerOpen] = useState(false);
    const [routingCount, setRoutingCount] = useState(0);
    const [isCheckout, setIsCheckout] = useState(false);
    const triggerDelay = 120000;
    let triggerTimeout = useRef(setTimeout(() => {}));
    let popupTimeout = useRef(setTimeout(() => {}));
    let loadInterval;

    const {
        config,
        salesChannel,
        currentLanguage: {
            code,
            languageCode
        }
    } = useSessionStore((state) => state.session);

    const {
		translated: {
			customFields
		}
	} = salesChannel;

    useEffect(() => {
        checkChatAvailability();

        setInterval(() => {
            checkChatAvailability();
        }, 60000);
    }, []);

    useEffect(() => {
        setIsCheckout(router.asPath === '/checkout');
        let matchedTrigger = matchPathWithTriggers(router.asPath);

        if(matchedTrigger && !checkTriggerCookie(matchedTrigger.priority)) {
            console.log('set trigger');
            triggerTimeout.current = setTimeout(() => {
                if(matchedTrigger && matchedTrigger.text) {
                    setTrigger(matchedTrigger);
                }
            }, triggerDelay);
        }

        if(code) {
            setRoutingCount(routingCount + 1);
        }

        return () => {
            clearTimeout(popupTimeout.current);
            clearTimeout(triggerTimeout.current);
        }
    }, [code, router.asPath]);

    useEffect(() => {
        if (isCheckout) {
            setTriggerOpen(false);
        }
    }, [isCheckout]);

    useEffect(() => {
        if(shouldShowTrigger(trigger.priority)) {
            popupTimeout.current = setTimeout(() => {
                if(shouldShowTrigger(trigger.priority) && !isCheckout) {
                    setTriggerOpen(true);
                    setTriggeredCookie(trigger.priority);
                }
            }, 4000)
        }

        return () => {
            clearTimeout(popupTimeout.current);
        }
    }, [trigger, status]);

    useEffect(() => {
        if(routingCount > 2 && !chatLoaded) {
            loadZendeskChat();
        }
    }, [routingCount]);

    useEffect(() => {
        if(router.asPath.indexOf('#open-chat') >= 0) {
            if(status === 'online') {
                setPreChatForm({...preChatForm, open: true});
            } else {
                setOfflinePreChat(true);
            }

			router.replace(router.asPath.split('#')[0]);
        }
    }, [router.asPath, status]);

    function isInStoreDisplay() {
        return window?.navigator?.userAgent?.indexOf('NEWPORT_INSTOREBROWSER') >= 0 ? true : false;
    }

    async function checkChatAvailability() {
        try {
            const response = await fetch('/api/zendesk/chat/activity', {method: 'GET'});
            const data = await response.json();

            if (data.status === 'online' && !isInStoreDisplay()) {
                setStatus('online');
            } else {
                setStatus('offline');
            }
        } catch (e) {}
    }

    function loadZendeskChat(onChatLoaded) {
        if(!chatLoaded) {
            const script = document.createElement('script');
            script.id = 'ze-snippet'
            script.src = 'https://static.zdassets.com/ekr/snippet.js?key=b2d6f5f5-03df-42f7-b819-60974db1cee0';

            script.addEventListener('load', () => {
                setChatLoaded(true);

                if(typeof onChatLoaded !== 'undefined') {
                    onChatLoaded();
                }

                zE('webWidget:on', 'userEvent', () => {
                    setScriptLoading(false);
                });
            });

            document.body.appendChild(script);
        } else {
            onChatLoaded();
        }
    }

    function matchPathWithTriggers(path) {
        let matchedTrigger = false;
        triggerObject.triggers.forEach(function(trigger){
            if (trigger.matchType === 'start') {
                trigger.path.forEach((singlePath) => {
                    if(path.indexOf(singlePath) === 0) {
                        matchedTrigger = {
                            text: trigger.text,
                            tag: trigger.tag,
                            priority: trigger.priority
                        }
                    }
                });
            }

            if (trigger.matchType === 'contains') {
                trigger.path.forEach((singlePath) => {
                    if (path.indexOf(singlePath) >= 0) {
                        matchedTrigger = {
                            text: trigger.text,
                            tag: trigger.tag,
                            priority: trigger.priority
                        }
                    }
                });
            }

            if (trigger.matchType === 'full') {
                trigger.path.forEach((singlePath) => {
                    if (path === singlePath) {
                        matchedTrigger = {
                            text: trigger.text,
                            tag: trigger.tag,
                            priority: trigger.priority
                        }
                    }
                });
            }
        });

        return matchedTrigger;
    }

    function getCountryTag(locale) {
        switch (locale) {
            case 'nb':
                return 'norge';
            case 'fi':
                return 'finland';
            case 'sv':
                return 'sverige';
            case 'nl':
                return 'nederländerna';
            case 'da':
                return 'danmark';
            case 'de':
                return 'tyskland';
            case 'en':
            default:
                if(config.language.languageCode === 'en-US') {
                    return 'global';
                }

                return 'europa';
        }
    }

    function togglePreChatForm() {
        if(triggerOpen) {
            closeTrigger();
        }

        let previousChatFormEmail = Cookies.get('zendeskChatForm');

        if(!previousChatFormEmail) {
            setPreChatForm({...preChatForm, open: !preChatForm.open});
        } else {
            if(!chatLoaded) {
                setScriptLoading(true);
            }

            loadZendeskChat(() => {
                identifyAndTag(atob(previousChatFormEmail));
                openChat();
            });
        }
    }

    function updateEmail(e) {
        let value = e.target.value;

        setPreChatForm({...preChatForm, email: value});
    }

    function openChatFromPreChatForm() {
        let email = preChatForm.email;

        setScriptLoading(true);
        loadZendeskChat(() => {
            if (email.length > 0) {
                identifyAndTag(email);
            }

            openChat();
            setTrigger({text: false, tag: false});
        });

        trackingActions.click({type: 'chat_open', text: t('prechat-start'), identifier: router.asPath});
        setPreChatForm({...preChatForm, open: false});
    }

    function identifyAndTag(email) {
        zE('webWidget', 'identify', {
            name: email.split('@')[0],
            email: email
        });

        zE('webWidget', 'chat:addTags', 'chat-has-email-address');

        if(trigger) {
            zE('webWidget', 'chat:addTags', trigger.tag);
        }

        Cookies.set('zendeskChatForm', base64Encode(email));
    }

    function closeTrigger() {
        setTriggerOpen(false);
    }

    function shouldShowTrigger(priority) {
        if (status === 'online' && trigger.text && !checkTriggerCookie(priority) && !preChatForm.open) {
            return true;
        } else {
            return false;
        }
    }

    function openChat() {
        loadInterval = setInterval(() => {
            if(typeof window.zE.activate !== 'undefined') {
                zE.activate();
                clearInterval(loadInterval);
            }
        }, 100);
    }

    function setTriggeredCookie(priority) {
        Cookies.set('zendeskChatTrigger', priority, { expires: 2 });
    }

    function checkTriggerCookie(priority) {
        let cookiePriority = Cookies.get('zendeskChatTrigger') || 0;

        return priority <= cookiePriority;
    }

    return (
        <>
            <Head>
                <style>{`iframe#launcher {display: none;}`}</style>
                <script type="text/javascript" dangerouslySetInnerHTML={{
                    __html: `window.zESettings = {
                        webWidget: {
                            chat: {
                                concierge: {
                                    title: {
                                        '*': '${t("chat-title")}'
                                    }
                                }
                            }
                        }
                    }`
                }}/>
                {chatLoaded && languageCode ? <script type="text/javascript" dangerouslySetInnerHTML={{
                    __html: `
                        zE('webWidget', 'setLocale', '${languageCode.split("-")[1].toLowerCase()}');
                        zE('webWidget', 'chat:addTags', '${getCountryTag(code)}');
                    `
                }}/> : ''}
            </Head>

            <button className={[classes.chatBubble, 'chatBubble', status === 'online' ? 'active' : ''].join(' ')}
                    onClick={() => {
                        if (preChatForm.open) {
                            togglePreChatForm();
                        } else if (trigger.text && trigger.text.length > 0) {
                            setTriggerOpen(true);
                        } else {
                            togglePreChatForm();
                        }
                    }}>
                {trigger.text && !triggerOpen && !checkTriggerCookie(trigger.priority) && !preChatForm.open && !isCheckout ?
                    <div className={classes.typing}>
                        <div className={classes.dot}/>
                        <div className={classes.dot}/>
                        <div className={classes.dot}/>
                    </div> : ''}

                <span className={[classes.profiles, 'profile'].join(' ')}>
                    {scriptLoading ? <CircularProgress style={{
                        position: 'absolute',
                        top: 'calc(50% - 20px)',
                        left: 'calc(50% - 20px)',
                        color: theme.palette.primary.background,
                        zIndex: 100,
                        opacity: 0.75
                    }}/> : ''}
                    <span className={classes.profile}>
                        <img alt={'Newport Support'}
                             src={'https://cdn.newporthome.eu/images/chat/6P7A6987ellipsefinal.png'} width="60"/>
                    </span>
                </span>
                <span className={classes.onlineIndicator}/>
            </button>

            <div className={[classes.preChatForm, preChatForm.open ? 'open' : ''].join(' ')}>
                <p className={classes.title}>
                    <span className={classes.titleIndicator}/>
                    {t('prechat-title')}
                    <button type="button" className={classes.titleMinimize} onClick={() => {
                        togglePreChatForm()
                    }}>
                        <svg width="12" id="Layer_1" x="0px" y="0px" viewBox="0 0 192 192">
                            <style>{`.st0 {fill: #666;}`}</style>
                            <polygon className="st0"
                                     points="180.2,0 95.8,84.3 11.8,0.4 0,12.2 84,96 0,179.9 11.8,191.6 95.8,107.8 180.2,192 192,180.3 107.6,96 192,11.8"/>
                        </svg>
                    </button>
                </p>

                <div className={classes.fields}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#000000' }}><span
                        dangerouslySetInnerHTML={{ __html: t('prechat-intro') }}/></p>
                    <p style={{ marginTop: 5, fontSize: 13 }}>
                        {['se', 'no'].indexOf(config.language.locale) < 0 ? t('chat-disclaimer') + ' ' : ''}
                        <span dangerouslySetInnerHTML={{ __html: t('prechat-other-1') }}/><br/>
                        <Link href={customFields?.toplinks_customerservice_link || '/'}><a
                            style={{ textDecoration: 'underline', color: 'inherit' }}>{t('prechat-other-2')}</a></Link>
                    </p>

                    <label className={classes.label}>
                        <input className={classes.input} name="email" placeholder={t('prechat-email-placeholder')}
                               onChange={(e) => {
                                   updateEmail(e)
                               }} value={preChatForm.email}/>
                    </label>
                </div>

                <div className={classes.actions}>
                    <button className={classes.startChat} onClick={() => {
                        openChatFromPreChatForm()
                    }}>{t('prechat-start')}</button>
                </div>
            </div>

            <div className={[classes.preChatForm, offlinePreChat ? 'open' : ''].join(' ')}>
                <p className={classes.title}>
                    <span className={[classes.titleIndicator, 'offline'].join(' ')}/>
                    {t('prechat-title-offline')}
                    <button type="button" className={classes.titleMinimize} onClick={() => {
                        setOfflinePreChat(false);
                    }}>
                        <svg width="12" id="Layer_1" x="0px" y="0px" viewBox="0 0 192 192">
                            <style>{`.st0 {fill: #666;}`}</style>
                            <polygon className="st0"
                                     points="180.2,0 95.8,84.3 11.8,0.4 0,12.2 84,96 0,179.9 11.8,191.6 95.8,107.8 180.2,192 192,180.3 107.6,96 192,11.8"/>
                        </svg>
                    </button>
                </p>

                <div className={classes.fields}>
                    <p style={{ margin: 0, fontSize: 13 }}>
                        <span dangerouslySetInnerHTML={{ __html: t('prechat-intro-offline') }}></span>
                        <Link href={customFields?.toplinks_customerservice_link || '/'}><a style={{ textDecoration: 'underline', color: 'inherit' }}>{t('prechat-other-2')}</a></Link></p>
                </div>
            </div>

            <div className={[classes.preChatForm, triggerOpen ? 'open' : ''].join(' ')}>
                <p className={classes.title}>
                    <span className={classes.titleIndicator}/>
                    {t('prechat-title')}
                    <button type="button" className={classes.titleMinimize} onClick={() => {
                        closeTrigger();
                    }}>
                        <svg width="12" id="Layer_1" x="0px" y="0px" viewBox="0 0 192 192">
                            <style>{`.st0 {fill: #666;}`}</style>
                            <polygon className="st0"
                                     points="180.2,0 95.8,84.3 11.8,0.4 0,12.2 84,96 0,179.9 11.8,191.6 95.8,107.8 180.2,192 192,180.3 107.6,96 192,11.8"/>
                        </svg>
                    </button>
                </p>
                <div className={classes.triggerContent}>
                    <img alt={'Newport Support'} src="/images/np_chat_logo.png" width="32"
                         style={{ borderRadius: '50%', overflow: 'hidden' }}/>
                    <div className={classes.triggerText}>{t('chat-triggers:' + trigger.text)}</div>
                </div>
                {['se', 'no'].indexOf(config.language.locale) < 0 ?
                    <p className={classes.chatDisclaimer}>{t('chat-disclaimer')}</p> : ''}
                <div className={classes.triggerActions}>
                    <button className={classes.triggerClose} onClick={() => {
                        closeTrigger();
                    }}>{t('prechat-close')}</button>
                    <button className={classes.startChat} onClick={() => {
                        togglePreChatForm();
                    }} style={{ width: '50%' }}>{t('prechat-start')}</button>
                </div>
            </div>
        </>
    )
}
